import * as THREE from "three";
import * as PANOLENS from "panolens";
import {aircraftConfig, LufthansaColors, settings} from "../config";
import {getLAT, LATEventBus} from "./LAT/LAT_main";
import {createMarker} from "./LAT/panorama";
import {MeshLine, MeshLineMaterial} from 'three.meshline';
import Vue from "vue";
import {createCircleTexture, createRectTexture} from "./three/canvastexture";

export const TeachingMode = {
    guide: 'guide',
    exam: 'exam'
}
export const ExamMode = {
    blind: 'blind',
    guide: 'guide'
}

class ProcedureTeacher {

    activeProcedure = null
    populatedOjects = []
    stepIdxToObjectsMap = {}
    drawedLine = null
    drawLineSource2D = null
    drawLineTarget = null
    stepIdx = 0
    mode = TeachingMode.guide
    lineOpacity = 1
    infopointOpacity = 0
    lastReceivedHelplineSliderValue = 0
    examMode = ExamMode.guide;

    selectDefaultProcedure(force) {
        if (!force && this.activeProcedure) return this;
        return this.selectProcedure(aircraftConfig.procedures[0]);
    }

    endProcedure() {
        this.activeProcedure = null;
        this.clearView();
    }

    setMode(mode){
        if(mode !== this.mode){
            this.mode = mode;
            this.clearView()
            if(this.activeProcedure){
                this.populateView();
            }
            if(this.isExamMode()){
                LATEventBus.$emit('slider-change', {actionType: 'poi', value: 0})
                getLAT().lookAtStartPoint()
            }
        }
    }

    getCurrentStepPointVec() {
        const step = this.getStep();
        if (!step || !step.points || step.points.length === 0) return new THREE.Vector3(...settings.DEFAULT_LOOKAT_POS);
        return new THREE.Vector3(step.points[0].vec[0], step.points[0].vec[1], step.points[0].vec[2])
    }

    selectProcedure(procedure) {
        if (this.activeProcedure) {
            this.clearView();
        }
        this.activeProcedure = procedure;
        if (this.activeProcedure.steps) {
            this.activeProcedure.steps = this.activeProcedure.steps.map((step, stepIdx) => {
                if (step.points && step.points.length > 0 && step.points[0].vec === undefined) {
                    step.points = step.points.map(poiName => {
                        return aircraftConfig.getPOI(poiName);
                    })
                }
                return Object.assign(step, {stepIdx});
            })
        }
        if(this.isExamMode()){
            getLAT().lookAtStartPoint()
        }
        this.populateView(false);
        return this;
    }


    getStep() {
        if(!this.activeProcedure || this.stepIdx >= this.activeProcedure.steps.length) return null;
        return {
            stepIdx: this.stepIdx,
            ...this.activeProcedure.steps[this.stepIdx],
        }
    }

    getStepObjects() {
        return this.stepIdxToObjectsMap[this.stepIdx] ? this.stepIdxToObjectsMap[this.stepIdx] : [];
    }

    isExamMode(){
        return this.mode === 'exam';
    }

    setStep(idx) {
        if(this.getSteps().length <= idx) return null;
        this.stepIdx = idx;
        const step = this.getStep();
        if (step.panorama) {
            getLAT().textureSwitcher.changeTo(step.panorama)
        }
        if(!this.isExamMode()){
            getLAT().lookAtObject(this.getCurrentStepPointVec());
            this.setLineTarget(this.getCurrentStepPointVec().multiply(new THREE.Vector3(-1, 1, 1)))
        }
        return step;
    }

    isRunning(){
        return !!this.activeProcedure;
    }

    getSteps() {
        return this.activeProcedure ? this.activeProcedure.steps : [];
    }

    getPoints(inverted) {
        let result = [];
        if (!this.activeProcedure) return result;

        /* populate steps with data */
        this.activeProcedure.steps.forEach((pos, idx) => {
            result = result.concat(pos.points
                ? pos.points.map(p => Object.assign({stepIdx: idx}, p, {vec: new THREE.Vector3(...p.vec), parent: pos}))
                : [Object.assign({stepIdx: idx}, pos, {vec: new THREE.Vector3(...pos.vec)})]
            )
        });
        if (inverted) {
            result.forEach(point => {
                point.vec.x = point.vec.x * -1
            })
        }
        return result;
    }

    clearView() {
        const scene = getLAT().scene;
        this.populatedOjects.forEach(obj3D => {
            if (obj3D.parent) {
                obj3D.parent.remove(obj3D)
            } else {
                scene.remove(obj3D)
            }
        })
        this.clearDrawLine();
        this.stepIdxToObjectsMap = {};
        this.setLineTarget(null)
    }

    addObject3D(object3D, point) {
        this.populatedOjects.push(object3D);
        if (!this.stepIdxToObjectsMap[point.stepIdx]) {
            this.stepIdxToObjectsMap[point.stepIdx] = [];
        }
        this.stepIdxToObjectsMap[point.stepIdx].push(object3D)
    }

    getInfospots(){
        return this.populatedOjects.filter(obj => obj instanceof PANOLENS.Infospot);
    }

    clickedStepPoint(infospot, point) {
        if (this.stepIdx === point.stepIdx) {
            LATEventBus.$emit('correct_step_clicked', {point})
        } else {
            LATEventBus.$emit('wrong_step_clicked', {point})
        }
    }

    populateView(withDebug) {
        const scene = getLAT().scene;
        // function onFocus () {
        //     const parameters = {
        //         duration: 1000,
        //         curve: 'Exponential',
        //         easing: 'Out'
        //     };
        //     console.log('onfocus... bound', this)
        //     this.focus( parameters.duration, TWEEN.Easing[ parameters.curve ][ parameters.easing ] );
        // }
        this.getPoints(false).forEach(point => {
            Vue.$log.debug('Adding Infospot Point', point)

            const radius = 10000 / 2;
            const scale = settings.INFOPOINT_SCALE * point.vec.length() / radius
            const name = 'Step ' + (point.stepIdx + 1) + ': ' + point.name

            const createInfospot = ()=> {
                const infospot = createMarker(scale, name);
                infospot.position.copy(point.vec);

                // infospot is loading some texture src first, so we need to replace the image right after that happened...
                setTimeout(()=>{
                    infospot.material.map = createCircleTexture(64, false, 1, LufthansaColors.yellow);
                }, 100);

                infospot.addHoverText("")

                // setTimeout(()=>{
                //     const domElement = Array.from(document.getElementsByClassName( 'desc-container' ))
                //         .filter(el => {
                //             return Number(el.dataset.stepidx) === point.stepIdx
                //         }).pop();
                //     console.log('add hover element', domElement)
                //     infospot.addHoverElement( domElement, 200 );
                // }, 500) //need time to render the dom element

                infospot.addEventListener('click', () => {
                    this.clickedStepPoint(infospot, point);
                });

                infospot.addEventListener('hover', () => {

                    const timeoutKey = infospot.name + 'hover_timeout';

                    infospot.material.opacity = settings.INFOPOINT_MAX_TRANSPARENCY;
                    clearTimeout(window[timeoutKey]);
                    window[timeoutKey] = setTimeout(()=>{
                        delete window[timeoutKey];
                        infospot.material.opacity = this.infopointOpacity;
                    }, settings.HIDEINFOPOINTAFTERHOVER)



                });

                return infospot;
            }
            const infospot = createInfospot();
            infospot.createCopy = createInfospot;

            scene.add(infospot);
            this.addObject3D(infospot, point)
        })

        if (withDebug) {
            this.getPoints(true).forEach(point => {
                const geometry = new THREE.BoxGeometry(100, 100, 100);
                const material = new THREE.MeshBasicMaterial({color: 0x00ff00, opacity: 0});
                const cube = new THREE.Mesh(geometry, material);
                cube.position.copy(point.vec);

                cube.raycast = () => {
                }; //override raycasting = no intersections
                cube.setVisibility = (amount) => {
                    cube.material.opacity = getLAT().sliderToScale(amount)
                }
                scene.add(cube);
                this.populatedOjects.push(cube)
            })
        }
    }

    setLineSource2D(x, y) {
        this.drawLineSource2D = x === null || x === undefined ? null : [
            x + settings.MARKER_PADDING_LEFT,
            y + settings.MARKER_PADDING_TOP
        ]
    }

    setLineTarget(object3DorVec) {
        this.drawLineTarget = object3DorVec;
    }

    clearDrawLine() {
        if (this.drawedLine && this.drawedLine.mesh) {
            getLAT().scene.remove(this.drawedLine.mesh)
            this.drawedLine.mesh.parent.remove(this.drawedLine.mesh)
            this.drawedLine = null;
        }
    }

    drawLine() {

        if (!this.drawLineTarget || !this.drawLineSource2D) {
            if (this.drawedLine && this.drawedLine.mesh && this.drawedLine.mesh.material) {
                this.drawedLine.mesh.material.opacity = 0;
            }
            return
        }

        const targetVec = this.drawLineTarget.position ? this.drawLineTarget.position : this.drawLineTarget.clone();
        const mouse = this.drawLineSource2D ? getLAT().convert2Dto3Dcoordinate(...this.drawLineSource2D) : getLAT().mouseVector;
        const geometry = new THREE.Geometry().setFromPoints([
            mouse,
            targetVec
        ]);

        if (this.drawedLine && this.drawedLine.mesh && this.drawedLine.mesh.material) {
            this.drawedLine.mesh.material.opacity = this.lineOpacity;
            this.drawedLine.line.setGeometry(geometry);
        } else {

            const line = new MeshLine();
            line.setGeometry(geometry);
            const material = new MeshLineMaterial({
                near: 1,
                far: 10000,
                lineWidth: 0.025,
                sizeAttenuation: 1,
                depthTest: false,
                transparent: true,
                opacity: 0.5,
                color: settings.MARKER_LINE_COLOR
            });
            const mesh = new THREE.Mesh(line.geometry, material); // this syntax could definitely be improved!

            getLAT().scene.add(mesh);
            this.drawedLine = {
                mesh,
                line
            };

            window.drawedLine = this.drawedLine;
        }
    }

    setHelplineVisibility(value) {
        const percentValue = Math.min(getLAT().sliderToScale(value), this.mode === 'exam' ? 0 : 1);
        const startInfopointOpacityAt = 0.4
        const calculateRelativeOpacity = ()=> getLAT().sliderToScale(percentValue - startInfopointOpacityAt, 1 - startInfopointOpacityAt,0, settings.INFOPOINT_MAX_TRANSPARENCY)
        const opacityInfopoint = percentValue < startInfopointOpacityAt ? 0 : Math.min(calculateRelativeOpacity(), settings.INFOPOINT_MAX_TRANSPARENCY)

        this.lineOpacity = percentValue;
        this.infopointOpacity = opacityInfopoint;

        this.getInfospots().forEach(infospot => {
            infospot.addHoverText(percentValue > startInfopointOpacityAt ? infospot.name : '')
            infospot.material.opacity = opacityInfopoint;
        })
    }

    isBlindExam(){
        return this.mode === TeachingMode.exam && this.examMode === ExamMode.blind;
    }
}

export const procedureTeacher = new ProcedureTeacher();